import { AlertOctagon, CheckCircle2, Search, SearchCheck } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  computeIncidentSummary,
  INCIDENT_STATUS,
  SEVERITY,
} from './mockIncidentData';

function SeverityBadge({ severity }) {
  const config = SEVERITY[severity];
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ring-1 ${config.className}`}
    >
      {config.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const config = INCIDENT_STATUS[status];
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ring-1 ${config.className}`}
    >
      {config.label}
    </span>
  );
}

export default function ActiveIncidentsLedger({
  incidents,
  onInvestigate,
  onClose,
}) {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [closingId, setClosingId] = useState(null);
  const [closureNote, setClosureNote] = useState('');
  const summary = computeIncidentSummary(incidents);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return incidents.filter((inc) => {
      const matchesStatus = statusFilter === 'all' || inc.status === statusFilter;
      const matchesQuery =
        inc.id.toLowerCase().includes(q) ||
        inc.description.toLowerCase().includes(q) ||
        inc.actionTaken.toLowerCase().includes(q) ||
        SEVERITY[inc.severity].label.toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
  }, [incidents, query, statusFilter]);

  const handleCloseSubmit = (id) => {
    onClose?.(id, closureNote.trim());
    setClosingId(null);
    setClosureNote('');
  };

  return (
    <section className="overflow-hidden rounded-xl border border-logi-grid bg-white backdrop-blur-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-logi-grid px-4 py-3">
        <div className="flex items-center gap-2">
          <AlertOctagon className="h-4 w-4 text-red-700" strokeWidth={1.75} />
          <div>
            <h3 className="text-sm font-bold text-logi-text">Active Incidents Ledger</h3>
            <p className="text-[11px] text-logi-text0">
              {summary.open + summary.investigating} open/investigating · {summary.critical} critical
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 rounded-lg border border-logi-grid bg-white p-0.5">
            {['all', 'open', 'investigating', 'closed'].map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setStatusFilter(key)}
                className={`rounded-md px-2 py-1 text-[10px] font-bold uppercase transition ${
                  statusFilter === key
                    ? 'bg-logi-accent text-white'
                    : 'text-logi-text0 hover:text-logi-muted'
                }`}
              >
                {key}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-48">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-logi-text0" />
            <input
              type="search"
              placeholder="Search incidents…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded-lg border border-logi-grid bg-white py-1.5 pl-8 pr-3 text-xs text-logi-text placeholder-logi-subtle outline-none focus:border-red-500/50 focus:ring-1 focus:ring-red-500/30"
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[1020px] text-left text-sm">
          <thead>
            <tr className="border-b border-logi-grid/80 bg-white text-[10px] uppercase tracking-wider text-logi-text0">
              <th className="px-4 py-2.5 font-semibold">Incident ID</th>
              <th className="px-4 py-2.5 font-semibold">Severity</th>
              <th className="px-4 py-2.5 font-semibold">Date &amp; Time</th>
              <th className="px-4 py-2.5 font-semibold">Description</th>
              <th className="px-4 py-2.5 font-semibold">Action Taken</th>
              <th className="px-4 py-2.5 font-semibold">Status</th>
              <th className="px-4 py-2.5 font-semibold">Workflow</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-logi-grid/30">
            {filtered.map((inc) => {
              const isActive = inc.status === 'open' || inc.status === 'investigating';
              const isCritical = inc.severity === 'critical' && isActive;
              const isClosing = closingId === inc.id;

              return (
                <tr
                  key={inc.id}
                  className={`transition-colors hover:bg-logi-silver ${
                    isCritical ? 'bg-red-500/[0.04]' : isActive ? 'bg-amber-500/[0.02]' : ''
                  }`}
                >
                  <td className="px-4 py-2.5">
                    <span className="font-mono text-xs font-bold text-red-700/90">{inc.id}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <SeverityBadge severity={inc.severity} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-logi-muted">
                    {inc.dateTime}
                  </td>
                  <td className="max-w-[200px] px-4 py-2.5 text-xs text-logi-text">
                    {inc.description}
                  </td>
                  <td className="max-w-[220px] px-4 py-2.5 text-xs text-logi-muted">
                    {inc.actionTaken}
                  </td>
                  <td className="px-4 py-2.5">
                    <StatusBadge status={inc.status} />
                  </td>
                  <td className="px-4 py-2.5">
                    {inc.status === 'closed' ? (
                      <span className="text-[10px] text-logi-text0">—</span>
                    ) : isClosing ? (
                      <div className="flex min-w-[200px] flex-col gap-1.5">
                        <input
                          type="text"
                          placeholder="Closure note (optional)…"
                          value={closureNote}
                          onChange={(e) => setClosureNote(e.target.value)}
                          className="w-full rounded border border-logi-grid bg-white px-2 py-1 text-[10px] text-logi-text outline-none focus:border-emerald-500/50"
                        />
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => handleCloseSubmit(inc.id)}
                            className="inline-flex items-center gap-1 rounded bg-emerald-600/20 px-2 py-1 text-[10px] font-bold uppercase text-emerald-700 ring-1 ring-emerald-500/30 hover:bg-emerald-600/30"
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            Confirm Close
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setClosingId(null);
                              setClosureNote('');
                            }}
                            className="rounded px-2 py-1 text-[10px] text-logi-text0 hover:text-logi-muted"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {inc.status === 'open' && (
                          <button
                            type="button"
                            onClick={() => onInvestigate?.(inc.id)}
                            className="inline-flex items-center gap-1 rounded bg-amber-600/15 px-2 py-1 text-[10px] font-bold uppercase text-amber-700 ring-1 ring-amber-500/25 transition hover:bg-amber-600/25"
                          >
                            <SearchCheck className="h-3 w-3" />
                            Investigate
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setClosingId(inc.id)}
                          className="inline-flex items-center gap-1 rounded bg-emerald-600/15 px-2 py-1 text-[10px] font-bold uppercase text-emerald-700 ring-1 ring-emerald-500/25 transition hover:bg-emerald-600/25"
                        >
                          <CheckCircle2 className="h-3 w-3" />
                          Close
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <p className="px-4 py-8 text-center text-sm text-logi-text0">No incidents match your filter.</p>
      )}
    </section>
  );
}
